/**
 * @typedef {import('../types').BorderComponents} BorderComponents
 * @typedef {import('../types').BorderSide} BorderSide
 * @typedef {import('../types').BorderStyle} BorderStyle
 */

/**
 * Parses a CSS-like border string into its constituent parts.
 * Handles strings like "border: 1px solid #ccc" or "border-top: 2px dashed".
 * @param {string} borderString - The CSS border string to parse.
 * @returns {BorderComponents | null} An object with the parsed border components, or null if the string is invalid or empty.
 */
export function parseBorderString(borderString) {
  if (!borderString || typeof borderString !== 'string') {
    return null;
  }

  const normalizedString = borderString.toLowerCase().trim();
  /** @type {BorderSide} */
  let sides = 'all';
  let valueString = normalizedString;

  const sideMatch = normalizedString.match(/^(border-(top|right|bottom|left)):/);
  if (sideMatch) {
    sides = /** @type {BorderSide} */ (sideMatch[2]);
    valueString = normalizedString.substring(sideMatch[0].length).trim();
  } else if (normalizedString.startsWith('border:')) {
    valueString = normalizedString.substring('border:'.length).trim();
  }

  if (valueString === 'none') {
    return { sides, style: 'none' };
  }

  const parts = valueString.split(/\s+/);
  /** @type {BorderComponents} */
  const result = { sides };
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];

    // Try to parse width and units (e.g., 1px, 0.5em)
    const widthMatch = part.match(/^(\d*\.?\d+)(px|em|rem|vw|vh|%)$/);
    if (widthMatch) {
      result.width = parseFloat(widthMatch[1]);
      result.units = widthMatch[2];
      i++;
      continue;
    }

    // Check for border style keywords
    /** @type {BorderStyle[]} */
    const styleKeywords = [
      'none',
      'hidden',
      'dotted',
      'dashed',
      'solid',
      'double',
      'groove',
      'ridge',
      'inset',
      'outset'
    ];
    if (styleKeywords.includes(/** @type {BorderStyle} */ (part))) {
      result.style = /** @type {BorderStyle} */ (part);
      i++;
      continue;
    }

    // Check for color values (basic hex, rgb, rgba, hsl, hsla)
    if (
      part.startsWith('#') ||
      part.startsWith('rgb(') ||
      part.startsWith('rgba(') ||
      part.startsWith('hsl(') ||
      part.startsWith('hsla(')
    ) {
      result.color = part;
      i++;
      continue;
    }

    // If none of the above, move to the next part
    i++;
  }

  return Object.keys(result).length > 1 ? result : null;
}

/**
 * @type {import('./types').PropertyConfigs}
 */
export const propertyRanges = {
  defaults: {
    min: 0,
    max: 100,
    step: 1,
    unit: 'px'
  },
  'border-radius': {
    defaults: { min: 0, max: 50, step: 1, unit: 'px' },
    units: {
      '%': { min: 0, max: 100, step: 1 },
      px: { min: 0, max: 50, step: 1 },
      rem: { min: 0, max: 5, step: 0.1 }
    }
  },
  'border-width': {
    defaults: { min: 0, max: 20, step: 1, unit: 'px' },
    units: {
      px: { min: 0, max: 20, step: 1 },
      rem: { min: 0, max: 2, step: 0.1 }
    }
  },
  opacity: {
    defaults: { min: 0, max: 1, step: 0.01, unit: '' }, // Default to standard unitless number
    units: {
      '': { min: 0, max: 1, step: 0.01 }, // Unitless
      '%': { min: 0, max: 100, step: 1 }
    }
  },
  'font-size': {
    defaults: { min: 8, max: 96, step: 1, unit: 'px' },
    units: {
      px: { min: 8, max: 96, step: 1 },
      rem: { min: 0.5, max: 6, step: 0.1 },
      em: { min: 0.5, max: 6, step: 0.1 },
      '%': { min: 50, max: 400, step: 1 }
    }
  },
  'line-height': {
    defaults: { min: 0.8, max: 3, step: 0.1, unit: '' }, // Default to unitless multiplier
    units: {
      '': { min: 0.8, max: 3, step: 0.1 }, // Unitless
      rem: { min: 0.8, max: 4, step: 0.1 },
      px: { min: 10, max: 50, step: 1 }
    }
  },
  margin: {
    defaults: { min: 0, max: 100, step: 1, unit: 'px' },
    units: {
      px: { min: 0, max: 100, step: 1 },
      rem: { min: 0, max: 10, step: 0.1 },
      '%': { min: 0, max: 100, step: 1 }
    }
  },
  padding: {
    defaults: { min: 0, max: 100, step: 1, unit: 'px' },
    units: {
      px: { min: 0, max: 100, step: 1 },
      rem: { min: 0, max: 10, step: 0.1 },
      '%': { min: 0, max: 100, step: 1 }
    }
  },
  width: {
    defaults: { min: 0, max: 100, step: 1, unit: '%' },
    units: {
      '%': { min: 0, max: 100, step: 1 },
      px: { min: 0, max: 400, step: 1 },
      vw: { min: 0, max: 100, step: 1 }
    }
  },
  height: {
    defaults: { min: 0, max: 100, step: 1, unit: '%' },
    units: {
      '%': { min: 0, max: 100, step: 1 },
      px: { min: 0, max: 400, step: 1 },
      vh: { min: 0, max: 100, step: 1 }
    }
  }
};
